import React from "react";
import tutorialSteps from "@/data/tutorialSteps";
import { useAuth } from "@/contexts/AuthContext";
import { getTutorialStatus, updateTutorialStatus } from "@/api/users";
import queryClient from "@/lib/queryClient";
import { qk as accountQueryKeys } from "@/queries/account";
import {
  getStatusStorageKey,
  getIntroStorageKey,
  readIntroCompleted,
  writeIntroCompleted,
  readTutorialStatus,
  writeTutorialStatus,
  setTutorialLock,
  clearTutorialLock,
  purgeGuestTutorialStorage,
} from "@/utils/tutorialStorage";

const BACKEND_SYNC_DEFAULT = process.env.REACT_APP_TUTORIAL_SYNC !== "false";
const START_EVENT = "speedway:tutorial:start";
const END_EVENT = "speedway:tutorial:end";
const TUTORIAL_STATUS_QUERY_OPTIONS = {
  queryKey: accountQueryKeys.tutorials,
  queryFn: getTutorialStatus,
  staleTime: 5 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export function useTutorial(pageName) {
  const { user, ready } = useAuth() || {};
  const storageKey = React.useMemo(
    () => (user ? getStatusStorageKey(user) : null),
    [user]
  );
  const introStorageKey = React.useMemo(
    () => (user ? getIntroStorageKey(user) : null),
    [user]
  );

  const [status, setStatus] = React.useState(() =>
    storageKey ? readTutorialStatus(storageKey) : { introCompleted: false, pages: {} }
  );
  const statusRef = React.useRef(status);
  const [run, setRun] = React.useState(false);
  const [backendSyncEnabled, setBackendSyncEnabled] = React.useState(
    BACKEND_SYNC_DEFAULT
  );
  const [statusHydrated, setStatusHydrated] = React.useState(!BACKEND_SYNC_DEFAULT);
  const steps = React.useMemo(() => tutorialSteps[pageName] || [], [pageName]);

  const pageState = status.pages?.[pageName] || { completed: false, step: 0 };
  const [stepIndex, setStepIndex] = React.useState(pageState.step || 0);
  const [introCompleted, setIntroCompleted] = React.useState(() =>
    introStorageKey ? readIntroCompleted(introStorageKey) : false
  );

  React.useEffect(() => {
    if (!ready || !user) {
      const emptyStatus = { introCompleted: false, pages: {} };
      statusRef.current = emptyStatus;
      setStatus(emptyStatus);
      setStatusHydrated(false);
      return;
    }
    purgeGuestTutorialStorage();
    if (!storageKey) {
      const emptyStatus = { introCompleted: false, pages: {} };
      statusRef.current = emptyStatus;
      setStatus(emptyStatus);
      setStatusHydrated(!BACKEND_SYNC_DEFAULT);
      return;
    }
    const nextStatus = readTutorialStatus(storageKey);
    statusRef.current = nextStatus;
    setStatus(nextStatus);
    const hasLocalTutorialState =
      Boolean(nextStatus?.introCompleted) ||
      Boolean(Object.keys(nextStatus?.pages || {}).length) ||
      Boolean(introStorageKey ? readIntroCompleted(introStorageKey) : false);
    setStatusHydrated(hasLocalTutorialState || !BACKEND_SYNC_DEFAULT);
  }, [introStorageKey, ready, storageKey, user]);

  React.useEffect(() => {
    if (!ready || !user) {
      setRun(false);
    }
  }, [ready, user]);

  React.useEffect(() => {
    if (!ready || !user || !introStorageKey) {
      setIntroCompleted(false);
      return;
    }
    setIntroCompleted(readIntroCompleted(introStorageKey));
  }, [introStorageKey, ready, user]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!ready || !introStorageKey) return undefined;

    const handleIntroComplete = (event) => {
      if (event?.detail?.key && event.detail.key !== introStorageKey) return;
      setIntroCompleted(true);
      setStatusHydrated(true);
      setStatus((prev) => {
        if (prev.introCompleted) return prev;
        const next = {
          ...prev,
          introCompleted: true,
        };
        writeTutorialStatus(storageKey, next);
        statusRef.current = next;
        return next;
      });
    };
    const handleStorage = (event) => {
      if (event.key === introStorageKey && event.newValue === "done") {
        handleIntroComplete({ detail: { key: introStorageKey } });
      }
    };
    window.addEventListener("speedway:intro:completed", handleIntroComplete);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("speedway:intro:completed", handleIntroComplete);
      window.removeEventListener("storage", handleStorage);
    };
  }, [introStorageKey, ready, storageKey]);

  const loadStatusFromServer = React.useCallback(async () => {
    if (!ready || !user || !backendSyncEnabled) return;
    try {
      const response = await queryClient.fetchQuery(TUTORIAL_STATUS_QUERY_OPTIONS);
      const tutorial = response?.tutorial;
      if (tutorial) {
        const nextState = {
          introCompleted: Boolean(tutorial.introCompleted),
          pages: tutorial.pages || {},
        };
        if (storageKey) {
          writeTutorialStatus(storageKey, nextState);
        }
        statusRef.current = nextState;
        setStatus(nextState);
        if (nextState.introCompleted) {
          if (introStorageKey) {
            writeIntroCompleted(introStorageKey);
          }
          setIntroCompleted(true);
        }
      }
    } catch (err) {
      if (err?.status === 404) {
        setBackendSyncEnabled(false);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn("Kunde inte hämta tutorial-status", err);
    }
  }, [backendSyncEnabled, introStorageKey, ready, storageKey, user]);

  React.useEffect(() => {
    loadStatusFromServer();
  }, [loadStatusFromServer]);

  React.useEffect(() => {
    setStepIndex(pageState.step || 0);
  }, [pageState.step]);

  React.useEffect(() => {
    if (pageState.completed) {
      setRun(false);
    }
  }, [pageState.completed]);

  React.useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const updateStatus = React.useCallback(
    (updater) => {
      if (!storageKey) return undefined;
      const next = updater(statusRef.current);
      writeTutorialStatus(storageKey, next);
      statusRef.current = next;
      setStatus(next);
      return next;
    },
    [statusRef, storageKey]
  );

  const markStep = React.useCallback(
    (nextIndex) => {
      updateStatus((prev) => ({
        ...prev,
        pages: {
          ...prev.pages,
          [pageName]: {
            ...prev.pages?.[pageName],
            step: nextIndex,
            completed: false,
          },
        },
      }));
    },
    [pageName, updateStatus]
  );

  const markCompleted = React.useCallback(() =>
    updateStatus((prev) => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageName]: { completed: true, step: 0 },
      },
    })), [pageName, updateStatus]);

  const startTutorial = React.useCallback(() => {
    if (!steps.length) return;
    if (!ready || !user || !storageKey) return;
    if (!introCompleted) return;
    if (typeof window !== "undefined") {
      window.__speedwayPageTutorialActive = true;
      window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { pageName } }));
    }
    setTutorialLock(pageName);
    setRun(true);
    markStep(stepIndex || 0);
  }, [introCompleted, markStep, pageName, stepIndex, steps.length]);

  const stopTutorial = React.useCallback(() => {
    setRun(false);
    clearTutorialLock(pageName);
  }, [pageName]);

  const syncStatusWithServer = React.useCallback(
    async (overridePages) => {
      if (!ready || !user || !backendSyncEnabled) return;
      try {
        const payload = {
          tutorial: {
            introCompleted,
            pages: overridePages || statusRef.current.pages,
          },
        };
        const response = await updateTutorialStatus(payload);
        queryClient.setQueryData(accountQueryKeys.tutorials, response);
      } catch (err) {
        if (err?.status === 404) {
          setBackendSyncEnabled(false);
          return;
        }
        // eslint-disable-next-line no-console
        console.warn("Kunde inte synka tutorial-status", err);
      }
    },
    [backendSyncEnabled, introCompleted, ready, statusRef, user]
  );

  const goToStep = React.useCallback(
    (nextIndex) => {
      if (!steps.length) return;
      const safeIndex = Math.min(Math.max(nextIndex, 0), steps.length - 1);
      setStepIndex(safeIndex);
      markStep(safeIndex);
    },
    [markStep, steps.length]
  );

  const finishTutorial = React.useCallback(() => {
    setRun(false);
    const nextState = markCompleted();
    const nextPages = nextState?.pages;
    syncStatusWithServer(nextPages);
    clearTutorialLock(pageName);
  }, [markCompleted, pageName, syncStatusWithServer]);

  const nextStep = React.useCallback(() => {
    if (!steps.length) {
      finishTutorial();
      return;
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex >= steps.length) {
      finishTutorial();
    } else {
      goToStep(nextIndex);
    }
  }, [finishTutorial, goToStep, stepIndex, steps.length]);

  const previousStep = React.useCallback(() => {
    if (!steps.length) return;
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(prevIndex);
    }
  }, [goToStep, stepIndex, steps.length]);

  const skipTutorial = React.useCallback(() => {
    finishTutorial();
  }, [finishTutorial]);

  const prevRunRef = React.useRef(run);

  React.useEffect(() => {
    if (prevRunRef.current === run) return;
    prevRunRef.current = run;
    if (typeof window === "undefined") return;
    if (run) {
      if (!window.__speedwayPageTutorialActive) {
        window.__speedwayPageTutorialActive = true;
        window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { pageName } }));
      }
    } else {
      window.__speedwayPageTutorialActive = false;
      window.dispatchEvent(new CustomEvent(END_EVENT, { detail: { pageName } }));
    }
  }, [pageName, run]);

  React.useEffect(() => {
    if (!run) {
      clearTutorialLock(pageName);
    }
  }, [pageName, run]);


  React.useEffect(() => () => {
    if (typeof window === "undefined") return;
    if (prevRunRef.current) {
      window.__speedwayPageTutorialActive = false;
      window.dispatchEvent(new CustomEvent(END_EVENT, { detail: { pageName } }));
    }
    clearTutorialLock(pageName);
  }, [pageName]);

  return {
    steps,
    run,
    stepIndex,
    shouldAutoStart:
      ready && !!user && statusHydrated && introCompleted && !pageState.completed && steps.length > 0,
    startTutorial,
    stopTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    goToStep,
    finishTutorial,
    completed: Boolean(pageState.completed),
    status,
    loadStatusFromServer,
    syncStatusWithServer,
    introCompleted,
  };
}
export default useTutorial;
